import { Component, Input, OnInit } from '@angular/core';
import { HttpService, ResponseMessage, ServiceResponse } from '@sdbn/appclient';
import { DialogService, SelectFieldOption, TranslationService } from '@sdbn/common';
import { environment } from 'src/environments/environment';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { Station } from 'src/tmc-yard/client/model/station';
import { CompletionOption, MovementOrderProcessingStatus } from 'src/tmc-yard/client/model/station-usage.model';
import { TransportDetailMode } from 'src/tmc-yard/common/configuration/transport.configuration';
import { Transport, TransportStatus } from 'src/tmc-yard/common/model/transport.model';
import { CancelMovementOrder, CompletionOptionType, CreateMovementOrder, ForwardToProcessAction, MovementOrderMovingStrategy } from '../../model/action-tablet.model';
import { ActionTabletConfirmDialogComponent, ActionTabletConfirmDialogData } from '../action-tablet-confirm-dialog/action-tablet-confirm.dialog.component';
import { TransportOptionSelectionComponent, TransportOptionSelectionDialogData } from '../transport-option-selection/transport-option-selection.component';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-action-tablet-buttonbar',
  templateUrl: './action-tablet-buttonbar.component.html',
  styleUrls: ['./action-tablet-buttonbar.component.scss']
})
export class ActionTabletButtonbarComponent implements OnInit {


  TransportDetailMode = TransportDetailMode;

  isLoading = false;
  responseMessages: ResponseMessage[];

  transportDetails: Transport;

  defaultCompletionOption: CompletionOption;

  targetName: string;

  strategySelectionItems: SelectFieldOption[] = [
    new SelectFieldOption({ label: 'Tor frei', value: MovementOrderMovingStrategy.CLEAR_GATE }),
    new SelectFieldOption({ label: 'weniger Rangierbewegungen', value: MovementOrderMovingStrategy.LESS_MOVEMENT })
  ];

  form = new FormGroup({
    movementStrategy: new FormControl(this.strategySelectionItems[0].value)
  });

  @Input('transportDetails') set setTransportDetails(transportDetails: Transport) {
    this.transportDetails = transportDetails;
  }

  @Input() station: Station;

  @Input('defaultCompletionOption') set setDefaultCompletionOption(defaultCompletionOption: CompletionOption) {
    this.defaultCompletionOption = defaultCompletionOption;
    if (defaultCompletionOption?.targetStationSetId) {
      this.targetName = this.tmcContextService.findStationSetById(defaultCompletionOption.targetStationSetId).name;
    } else if (defaultCompletionOption?.targetStationId) {
      this.targetName = this.tmcContextService.findStationById(defaultCompletionOption.targetStationId).name;
    } else {
      this.targetName = '';
    }

  }

  get createMovementEnabled() {
    return !this.isLoading
      && this.station?.stationUsage?.createMovementOrderAllowed
      && (!this.station?.stationUsage.ongoingMovementFromStation && !this.station?.stationUsage.ongoingMovementToStation)
      && (this.transportDetails.processing?.status === TransportStatus.RETRIEVED || this.transportDetails.processing?.status === TransportStatus.WAITING);
  }

  get movingOrderCancable() {
    return this.station?.stationUsage?.ongoingMovementProcessingStatus === MovementOrderProcessingStatus.OPEN;
  }

  constructor(
    public translationService: TranslationService,
    private tmcContextService: TmcContextService,
    private httpService: HttpService,
    private dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {

  }

  cancelOngoingMovementOrder() {
    const dto = new CancelMovementOrder();
    dto.currentStationId = this.station.id;
    dto.transportId = this.transportDetails.id;
    dto.ongoingMovementFromStation = this.station.stationUsage.ongoingMovementFromStation;
    dto.ongoingMovementToStation = this.station.stationUsage.ongoingMovementToStation;

    this.isLoading = true;
    this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/cancel-movement-order', dto).subscribe(response => {
      this.responseMessages = response.responseMessages;
      setTimeout(() => this.tmcContextService.forceRefreshOfStationUsages(), 500);
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
    });
  }

  completeNoFurtherProcessing() {
    const dto = new CreateMovementOrder();
    dto.currentStationId = this.station.id;
    dto.transportId = this.transportDetails.id;
    dto.completionOption = CompletionOptionType.NO_FURTHER_PROCESSING;
    dto.movementStrategy = this.form.controls.movementStrategy.value;

    this.isLoading = true;
    this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/create-movement-order', dto).subscribe(response => {
      this.responseMessages = response.responseMessages;
      setTimeout(() => this.tmcContextService.forceRefreshOfStationUsages(), 500);
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
    });
  }

  commpleteOnWaitingArea() {
    const dto = new CreateMovementOrder();
    dto.currentStationId = this.station.id;
    dto.transportId = this.transportDetails.id;
    dto.completionOption = CompletionOptionType.COMPLETE_ON_WAITING_AREA;
    dto.movementStrategy = this.form.controls.movementStrategy.value;

    this.isLoading = true;
    this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/create-movement-order', dto).subscribe(response => {
      this.responseMessages = response.responseMessages;
      setTimeout(() => this.tmcContextService.forceRefreshOfStationUsages(), 500);
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
    });
  }

  forwardToProcess() {
    this.responseMessages = [];

    const dialogData = new TransportOptionSelectionDialogData();
    dialogData.carrier = this.transportDetails.masterdata.carrier;
    dialogData.driver = this.transportDetails.masterdata.driverName;
    dialogData.transportScope = this.transportDetails.scope;
    dialogData.identifier = this.transportDetails.identifyingNumberplate;

    this.dialogService.openMiddle(TransportOptionSelectionComponent, dialogData).afterClosed().subscribe(result => {

      if (result != null) {

        const action = new ForwardToProcessAction();
        action.currentStationId = this.station.id,
          action.transportId = this.transportDetails.id;
        action.transportOptionIds = result.selectedOptionIds;

        this.isLoading = true;

        this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/forward-to-process', action).subscribe(result => {
          this.isLoading = false;
          this.responseMessages = result.responseMessages;
          setTimeout(() => this.tmcContextService.forceRefreshOfStationUsages(), 500);
        }, error => {
          console.error(error);
          this.isLoading = false;
        });

      }

    });
  }

  forwardToNextProcessingStation() {
    const dialogData: ActionTabletConfirmDialogData = {
      titleKey: 'tmc-yard.action-tablet.transport-details.confirm.create-movement-order.title',
      messageKey: 'tmc-yard.action-tablet.transport-details.confirm.create-movement-order.message',
      confirmBtnKey: 'tmc-yard.action-tablet.transport-details.confirm.create-movement-order.confirm-label',
      confirm: () => {
        const dto = new CreateMovementOrder();
        dto.currentStationId = this.station.id;
        dto.transportId = this.transportDetails.id;
        dto.completionOption = CompletionOptionType.FORWARD_TO_NEXT_PROCESSING_STATION;
        dto.movementStrategy = this.form.controls.movementStrategy.value;
        this.isLoading = true;
        this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/create-movement-order', dto).subscribe(response => {
          this.responseMessages = response.responseMessages;
          setTimeout(() => this.tmcContextService.forceRefreshOfStationUsages(), 500);
          this.isLoading = false;
        }, error => {
          this.isLoading = false;
        });
      }
    };

    this.dialogService.openDefault(ActionTabletConfirmDialogComponent, dialogData);
  }

}
